<template>
  <div :key="kind" class="order-records-page">
    <a-card :bordered="false" :title="recordType.title">
      <template #extra><span class="range-label">{{ appliedRangeLabel }}</span></template>
      <a-form v-if="kind === 'payment-records'" class="payment-search-filter" layout="inline" :model="searchFilter" @submit.prevent>
        <a-form-item label="手机号" html-for="payment-phone-num">
          <a-input id="payment-phone-num" v-model:value="searchFilter.phoneNum" allow-clear inputmode="tel" placeholder="精确匹配" @pressEnter.prevent="searchRecords" />
        </a-form-item>
        <a-form-item label="用户ID" html-for="payment-user-id">
          <a-input id="payment-user-id" v-model:value="searchFilter.userId" allow-clear inputmode="numeric" placeholder="精确匹配" @pressEnter.prevent="searchRecords" />
        </a-form-item>
        <a-form-item label="订单编号" html-for="payment-out-trade-no" class="order-number-filter">
          <a-input id="payment-out-trade-no" v-model:value="searchFilter.outTradeNo" allow-clear placeholder="精确匹配" @pressEnter.prevent="searchRecords" />
        </a-form-item>
      </a-form>
      <RecordDateFilter :model-value="filter" v-model:quick-value="quickDate" :loading="loading"
        @update:model-value="Object.assign(filter, $event)" @quick-change="handleQuickDate" @search="searchRecords" @reset="resetRecords" />
      <p class="record-note">{{ recordType.note }} 点击行首 + 查看详情。</p>
      <a-result v-if="loadError" status="warning" :title="`${recordType.title}加载失败`" :sub-title="loadError">
        <template #extra><a-button type="primary" :loading="loading" @click="fetchRecords(pagination.current, pagination.pageSize)">重新加载</a-button></template>
      </a-result>
      <a-table v-else :columns="columns" :data-source="records" :loading="loading" :pagination="false"
        :expanded-row-keys="expandedRowKeys" row-key="id" :scroll="{ x: tableWidth }" size="small" bordered @expand="handleExpand">
        <template #bodyCell="{ column, record, text }">
          <template v-if="column.amount">{{ formatRecordAmount(record[column.key], record.currency) }}</template>
          <PaymentMethodTag v-else-if="column.providerTradeType || column.paymentType" :record="record" :field="column" />
          <a-tag v-else-if="column.key === 'isPaid'" :color="record.isPaid === true ? 'green' : 'default'">{{ paymentState(record) }}</a-tag>
          <a-tooltip v-else-if="column.key === 'payResult'" :title="formatRecordText(text)">
            <span class="failure-reason">{{ formatRecordText(text) }}</span>
          </a-tooltip>
          <template v-else>{{ formatRecordText(text) }}</template>
        </template>
        <template #expandedRowRender="{ record }">
          <div class="record-expanded">
            <div v-if="detailStates[record.id]?.loading" class="detail-loading" role="status"><a-spin /><span>正在加载详情</span></div>
            <div v-else-if="detailStates[record.id]?.error" class="detail-error" role="alert">
              <span>详情加载失败：{{ detailStates[record.id].error }}</span>
              <a-button size="small" @click="loadRecordDetail(record)">重新加载</a-button>
            </div>
            <template v-else-if="detailStates[record.id]?.record">
              <RecordFieldSummary title="记录详情" :record="record" :fields="recordType.detailFields" />
              <RecordFieldSummary v-if="kind === 'pay-orders'" title="最近一条支付成功记录" :record="record.latestPayment"
                :fields="paymentSummaryFields" :empty-text="record.isPaid ? '暂无关联支付记录' : '暂无支付成功记录'" />
              <template v-else>
                <RecordFieldSummary title="关联下单记录" :record="record.payOrder" :fields="payOrderSummaryFields" empty-text="未找到对应下单记录" />
                <section class="agreement-summary">
                  <h3>关联签约记录 <a-tag :color="agreementState(record).color">{{ agreementState(record).label }}</a-tag></h3>
                  <p class="original-transaction">原始交易ID：<span>{{ formatRecordText(record.originalTransactionId) }}</span></p>
                  <RecordFieldSummary v-if="record.agreementStatus === 'matched' && record.agreement" :record="record.agreement" :fields="agreementSummaryFields" />
                  <p v-else class="association-message">{{ agreementState(record).description }}</p>
                </section>
              </template>
            </template>
          </div>
        </template>
        <template #emptyText><a-empty :description="`当前条件暂无${recordType.title}`" /></template>
      </a-table>
      <div class="record-pagination">
        <label class="page-size-control">每页
          <a-select :value="pagination.pageSize" :disabled="loading" :options="pageSizeOptions" aria-label="每页记录数" @change="changePageSize" />
        </label>
        <span class="page-summary" aria-live="polite">第 {{ pagination.current }} 页 · 本页 {{ pagination.dataCount }} 条</span>
        <a-button :disabled="loading || pagination.current === 1" @click="previousPage">上一页</a-button>
        <a-button :disabled="loading || !!loadError || !pagination.hasMore" @click="nextPage">下一页</a-button>
      </div>
    </a-card>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { get } from "@/utils/request";
import { analyticsUrl } from "@/utils/api";
import RecordDateFilter from "@/components/RecordDateFilter.vue";
import RecordFieldSummary from "@/components/RecordFieldSummary.vue";
import PaymentMethodTag from "@/components/PaymentMethodTag.vue";
import { getQuickRange, getDateRangeError, formatRecordRange } from "@/utils/recordDates";
import {
  orderRecordTypes, payOrderSummaryFields, paymentSummaryFields, agreementSummaryFields,
  formatRecordText, formatRecordAmount, paymentState, agreementState,
} from "@/utils/orderRecordTypes";

const props = defineProps({ kind: { type: String, required: true, validator: (value) => Object.hasOwn(orderRecordTypes, value) } });
const recordType = computed(() => orderRecordTypes[props.kind]);
const emptyRange = () => ({ startDate: "", endDate: "" });
const filter = reactive(emptyRange());
const appliedRange = ref(emptyRange());
const emptySearch = () => ({ phoneNum: "", userId: "", outTradeNo: "" });
const searchFilter = reactive(emptySearch());
const appliedSearch = ref({});
const appliedRangeLabel = computed(() => {
  const label = formatRecordRange(appliedRange.value);
  if (!Object.keys(appliedSearch.value).length) return label;
  const dateLabel = appliedRange.value.startDate || appliedRange.value.endDate ? label : "全部日期";
  return `${dateLabel} · 已筛选`;
});
const quickDate = ref(null);
const loading = ref(false);
const loadError = ref("");
const records = ref([]);
let requestSequence = 0;
const pagination = reactive({ current: 1, pageSize: 50, dataCount: 0, hasMore: false });
const pageSizeOptions = [20, 50, 100, 200].map((value) => ({ value, label: `${value} 条` }));
const pageCursors = ref([null]);
const expandedRowKeys = ref([]);
const detailStates = ref({});
const detailRequests = new Map();
const clearDetails = () => {
  expandedRowKeys.value = [];
  detailStates.value = {};
  detailRequests.clear();
};
const columns = computed(() => recordType.value.columns.map((column) => ({
  ...column, customCell: () => ({ style: { whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" } }),
})));
const tableWidth = computed(() => columns.value.reduce((width, column) => width + column.width, 60));

const normalizeSearch = (search) => Object.fromEntries(Object.keys(emptySearch())
  .map((key) => [key, String(search[key] ?? "").trim()]).filter(([, value]) => value !== ""));
const getSearchError = (search) => {
  if (search.phoneNum?.length > 11) return "手机号最多11个字符";
  if (search.outTradeNo?.length > 255) return "订单编号最多255个字符";
  if (search.userId) {
    const digits = search.userId.replace(/^0+/, "");
    if (!/^\d+$/.test(search.userId) || !digits || digits.length > 19 || (digits.length === 19 && digits > "9223372036854775807")) {
      return "用户ID须为不超过9223372036854775807的正整数";
    }
  }
  return "";
};
const fetchRecords = async (pageIdx = 1, pageSize = pagination.pageSize, range = appliedRange.value, reset = false, search = appliedSearch.value) => {
  const requestRange = { startDate: range.startDate || "", endDate: range.endDate || "" };
  const requestSearch = props.kind === "payment-records" ? normalizeSearch(search) : {};
  const validationError = getDateRangeError(requestRange, true) || getSearchError(requestSearch);
  if (validationError) { message.warning(validationError); return; }
  if (reset || pageSize !== pagination.pageSize) { pageCursors.value = [null]; pageIdx = 1; }
  const beforeId = pageCursors.value[pageIdx - 1];
  if (beforeId === undefined) return;
  const sequence = ++requestSequence;
  clearDetails();
  appliedRange.value = requestRange;
  appliedSearch.value = requestSearch;
  pagination.current = pageIdx;
  pagination.pageSize = pageSize;
  pagination.dataCount = 0;
  pagination.hasMore = false;
  loading.value = true;
  loadError.value = "";
  records.value = [];
  try {
    const params = { ...requestRange, ...requestSearch, paginationMode: "cursor", pageSize };
    if (beforeId !== null) params.beforeId = beforeId;
    const json = await get(analyticsUrl(recordType.value.listPath), params);
    if (sequence !== requestSequence) return;
    if (json.code !== 200 || !Array.isArray(json.data?.data)) throw new Error(json.content || "记录数据格式错误");
    records.value = json.data.data;
    pagination.dataCount = Number(json.data.dataCount ?? records.value.length);
    pagination.hasMore = json.data.hasMore === true && json.data.nextBeforeId !== null && json.data.nextBeforeId !== undefined && json.data.nextBeforeId !== "";
    pageCursors.value = pageCursors.value.slice(0, pageIdx);
    if (pagination.hasMore) pageCursors.value.push(json.data.nextBeforeId);
  } catch (error) {
    if (sequence === requestSequence) loadError.value = error?.message || "请求失败，请稍后重试";
  } finally {
    if (sequence === requestSequence) loading.value = false;
  }
};
const searchRecords = () => fetchRecords(1, pagination.pageSize, filter, true, searchFilter);
const previousPage = () => {
  if (!loading.value && pagination.current > 1) return fetchRecords(pagination.current - 1);
};
const nextPage = () => {
  if (!loading.value && !loadError.value && pagination.hasMore) return fetchRecords(pagination.current + 1);
};
const changePageSize = (pageSize) => fetchRecords(1, pageSize, appliedRange.value, true);
const loadRecordDetail = (record) => {
  const key = String(record.id);
  if (loading.value || !records.value.some((item) => String(item.id) === key)) return Promise.resolve();
  if (detailStates.value[key]?.record) return Promise.resolve(detailStates.value[key].record);
  if (detailRequests.has(key)) return detailRequests.get(key).promise;
  const sequence = requestSequence;
  const state = reactive({ loading: true, error: "", record: null });
  detailStates.value[key] = state;
  const request = { promise: null };
  detailRequests.set(key, request);
  const pending = (async () => {
    try {
      const json = await get(analyticsUrl(recordType.value.detailPath), { id: record.id });
      if (sequence !== requestSequence) return;
      if (json.code !== 200 || !json.data || String(json.data.id) !== key) throw new Error(json.content || "详情数据格式错误");
      state.record = { ...record, ...json.data };
      records.value = records.value.map((item) => String(item.id) === key ? state.record : item);
    } catch (error) {
      if (sequence === requestSequence) state.error = error?.message || "请求失败，请稍后重试";
    } finally {
      if (sequence === requestSequence) state.loading = false;
      if (detailRequests.get(key) === request) detailRequests.delete(key);
    }
  })();
  request.promise = pending;
  return pending;
};
const handleExpand = (expanded, record) => {
  expandedRowKeys.value = expandedRowKeys.value.filter((key) => key !== record.id);
  if (expanded) {
    expandedRowKeys.value.push(record.id);
    return loadRecordDetail(record);
  }
};
const handleQuickDate = ({ target }) => {
  const range = getQuickRange(target.value);
  if (!range) return;
  Object.assign(filter, range);
  return searchRecords();
};
const resetRecords = () => {
  quickDate.value = null;
  Object.assign(filter, emptyRange());
  Object.assign(searchFilter, emptySearch());
  return searchRecords();
};
watch(() => props.kind, () => {
  requestSequence += 1;
  pagination.current = 1;
  pagination.pageSize = 50;
  resetRecords();
}, { flush: "sync" });
onMounted(() => { fetchRecords(); });
onUnmounted(() => { requestSequence += 1; });
</script>

<style scoped>
.order-records-page { min-width: 0; }
.payment-search-filter { row-gap: 12px; margin-bottom: 12px; }
.payment-search-filter :deep(.ant-form-item) { margin-bottom: 0; }
.payment-search-filter :deep(.ant-input-affix-wrapper) { width: 200px; }
.payment-search-filter .order-number-filter :deep(.ant-input-affix-wrapper) { width: 260px; }
.range-label { color: rgba(0, 0, 0, 0.65); font-size: 13px; font-variant-numeric: tabular-nums; }
.record-note { margin: 8px 0 20px; color: rgba(0, 0, 0, 0.65); font-size: 13px; line-height: 1.7; }
.record-expanded { max-width: 1200px; }
.detail-loading, .detail-error { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; padding: 16px 0; }
.detail-error { color: #cf1322; }
.record-pagination { display: flex; align-items: center; justify-content: flex-end; flex-wrap: wrap; gap: 12px; padding-top: 20px; }
.page-size-control { display: flex; align-items: center; gap: 8px; }
.page-size-control :deep(.ant-select) { width: 96px; }
.page-summary { color: rgba(0, 0, 0, 0.65); font-variant-numeric: tabular-nums; }
.failure-reason { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.agreement-summary h3 { margin: 0 0 10px; font-size: 14px; font-weight: 600; }
.original-transaction { margin: 0 0 12px; }
.original-transaction span { font-variant-numeric: tabular-nums; white-space: pre-wrap; overflow-wrap: anywhere; }
.association-message { color: rgba(0, 0, 0, 0.65); }
@media (max-width: 768px) {
  .payment-search-filter { display: block; }
  .payment-search-filter :deep(.ant-form-item) { display: flex; margin: 0 0 12px; }
  .payment-search-filter :deep(.ant-form-item-label) { flex: 0 0 70px; }
  .payment-search-filter :deep(.ant-form-item-control) { min-width: 0; flex: 1; }
  .payment-search-filter :deep(.ant-input-affix-wrapper), .payment-search-filter .order-number-filter :deep(.ant-input-affix-wrapper) { width: 100%; min-height: 44px; }
  .order-records-page :deep(.ant-card-body) { padding: 16px; }
  .order-records-page :deep(.ant-card-head-wrapper) { align-items: flex-start; flex-wrap: wrap; gap: 4px; padding-block: 12px; }
  .order-records-page :deep(.ant-card-extra) { margin-inline-start: 0; }
  .record-expanded { width: calc(100vw - 104px); }
}
</style>
